// ScopedActionBacklog — actions filtered to the selected signal by default,
// with a toggle to view all actions for the week.

import React, { useMemo, useState } from "react";
import { actionsForTrack, groupActionsByPriority, normaliseRecommendedAction, sortBacklog } from "../../../lib/clients";
import RecommendedActionCard from "./RecommendedActionCard";

const PRIORITY_TONE = { HIGH: "negative", MEDIUM: "warning", LOW: "muted" };
const PRIORITY_LABEL = { HIGH: "High priority", MEDIUM: "Medium priority", LOW: "Low priority" };

const Badge = ({ tone = "muted", children }) => (
    <span className={`badge ${tone === "accent" ? "badge-accent" : tone === "positive" ? "badge-positive" : tone === "warning" ? "badge-warning" : tone === "negative" ? "badge-negative" : tone === "regulatory" ? "badge-regulatory" : tone === "ink" ? "badge-ink" : ""}`}>
        {children}
    </span>
);

const ToggleGroup = ({ value, onChange, options, testId }) => (
    <div className="inline-flex border border-[hsl(var(--line))] rounded-sm overflow-hidden" role="tablist" data-testid={testId}>
        {options.map((o) => (
            <button
                key={o.value}
                type="button"
                role="tab"
                aria-selected={value === o.value}
                onClick={() => onChange(o.value)}
                className={`px-3 py-1.5 text-[11px] font-medium transition-colors ${value === o.value ? "bg-[hsl(var(--ink))] text-white" : "bg-[hsl(var(--surface))] text-[hsl(var(--ink-soft))] hover:bg-[hsl(var(--surface-sunken))]"}`}
                data-testid={`${testId}-opt-${o.value}`}
            >
                {o.label}
            </button>
        ))}
    </div>
);

const ScopedActionBacklog = ({ selectedTrack, actionBacklog, onSelectTrack, trackCardsById }) => {
    const [scope, setScope] = useState("selected");

    const actions = useMemo(() => {
        if (scope === "all") return sortBacklog(actionBacklog || []).map(normaliseRecommendedAction).filter(Boolean);
        const raw = actionsForTrack({ selectedTrack, actionBacklog });
        return sortBacklog(raw).map(normaliseRecommendedAction).filter(Boolean);
    }, [scope, selectedTrack, actionBacklog]);

    const grouped = useMemo(() => groupActionsByPriority(actions), [actions]);
    const scopeLabel = scope === "selected"
        ? (selectedTrack ? `linked to ${selectedTrack.stableTrackName || selectedTrack.trackId}` : "for selected signal")
        : "across all signals";

    return (
        <section className="card-flat p-6" data-testid="action-backlog">
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="eyebrow">Action backlog</div>
                    <h3 className="display text-xl mt-1">What to do, in what order</h3>
                    <p className="text-[11px] text-[hsl(var(--mute))] mt-1">Grouped by priority, then time horizon — {scopeLabel}.</p>
                </div>
                <ToggleGroup
                    value={scope}
                    onChange={setScope}
                    options={[
                        { value: "selected", label: "For selected signal" },
                        { value: "all", label: "All actions" },
                    ]}
                    testId="backlog-scope"
                />
            </header>

            {grouped.length === 0 ? (
                <p className="text-sm text-[hsl(var(--mute))] mt-5" data-testid="action-backlog-empty">
                    {scope === "selected" ? "No related actions linked to this signal." : "No actions in the backlog for this week."}
                </p>
            ) : (
                <div className="mt-5 space-y-6">
                    {grouped.map((g) => (
                        <div key={g.priority} data-testid={`action-priority-${g.priority}`}>
                            <div className="flex items-baseline gap-2 mb-3">
                                <Badge tone={PRIORITY_TONE[g.priority] || "muted"}>{PRIORITY_LABEL[g.priority] || g.priority}</Badge>
                                <span className="text-[11px] text-[hsl(var(--mute))] num">{g.actions.length} action{g.actions.length === 1 ? "" : "s"}</span>
                            </div>
                            <ul className="space-y-3">
                                {g.actions.map((a, i) => (
                                    <li key={a.actionId || `${g.priority}-${i}`}>
                                        <RecommendedActionCard action={a} onSelectTrack={onSelectTrack} trackCardsById={trackCardsById} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
};

export default ScopedActionBacklog;
